import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, StatusPill, EmptyState } from "../../components/Common";
import { ArrowLeft, PlayCircle, CheckCircle2, RotateCcw, TrendingUp, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

/** Promotion360 — run rules, review + approve decisions.
 *  Backed by the deterministic evidence-based rule engine
 *  (PROMOTED / CONDITIONAL / RETAINED / REVIEW_REQUIRED). */
export default function Promotion() {
  const { school } = useParams();
  const { tenant } = useAuth();
  const nav = useNavigate();
  const academicYear = tenant?.academic_year || "2026";
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(null); // student_id
  const [year, setYear] = useState("Year 11");
  const [rules, setRules] = useState({ min_attendance_pct: 80, min_overall_avg: 50, min_subjects_passed: 4, conditional_overall_avg: 40 });
  const [busy, setBusy] = useState(false);

  const load = () => acAPI.listPromotion({ academic_year: academicYear }).then(({ data }) => setItems(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [academicYear]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await acAPI.runPromotion({ academic_year: academicYear, year_group: year, rules });
      toast.success(`Evaluated ${data.length} students in ${year}`);
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const approve = async (id) => {
    try { await acAPI.approvePromotion(id, { approved: true }); toast.success("Approved"); load(); }
    catch { toast.error("Failed"); }
  };

  if (items === null) return <Loading />;

  const stats = items.reduce((acc, p) => {
    acc[p.outcome] = (acc[p.outcome] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="promotion">
      <PageHeader
        kicker="Academic360™ · Promotion360"
        title="Promotion decisions"
        subtitle="Rules engine — attendance %, minimum grade, subjects passed. All decisions are advisory until Principal approves."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ["projected_promoted", "Projected Promoted"],
          ["projected_conditional", "Projected Conditional"],
          ["retained", "Retained"],
          ["review_required", "Review Required"],
          ["projected_graduated", "Projected Graduation"],
          ["promoted", "Promoted (final)"],
          ["conditional", "Conditional (final)"],
          ["graduated", "Graduated (final)"],
        ].map(([o, label]) => (
          <div key={o} className="hl-card p-4" data-testid={`stat-${o}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">{stats[o] || 0}</div>
          </div>
        ))}
      </div>
      <div className="hl-card p-3 mb-6 text-xs text-slate-600 flex items-start gap-2" data-testid="policy-note">
        <Info className="w-3.5 h-3.5 mt-0.5 text-[color:var(--hl-navy)]" />
        <div>
          <b>Deterministic rule engine · Temporal governance.</b> Decisions are calculated from canonical Grade Book evidence
          (First Term + Second Term aggregate) plus attendance and core-subject compliance.
          Because AY {academicYear} · Third Term is still in progress, all otherwise-passing outcomes are
          rendered as <span className="mono">projected_*</span> — final <b>PROMOTED / GRADUATED</b> requires
          the promotion window (Third Term reporting) to close or an authorised Principal override.
          AI narrative may explain outcomes but never determines them.
        </div>
      </div>

      <Section title="Run promotion evaluation">
        <div className="hl-card p-4 grid grid-cols-1 md:grid-cols-6 gap-2 text-sm items-end" data-testid="run-form">
          <select value={year} onChange={e => setYear(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="year-picker">
            {["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"].map(y => <option key={y}>{y}</option>)}
          </select>
          <label className="text-xs">Min attendance %<input type="number" value={rules.min_attendance_pct} onChange={e => setRules({ ...rules, min_attendance_pct: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="rule-attendance" /></label>
          <label className="text-xs">Min overall avg<input type="number" value={rules.min_overall_avg} onChange={e => setRules({ ...rules, min_overall_avg: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="rule-grade" /></label>
          <label className="text-xs">Min subjects passed<input type="number" value={rules.min_subjects_passed} onChange={e => setRules({ ...rules, min_subjects_passed: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="rule-subjects" /></label>
          <label className="text-xs">Conditional threshold<input type="number" value={rules.conditional_overall_avg} onChange={e => setRules({ ...rules, conditional_overall_avg: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="rule-conditional" /></label>
          <button onClick={run} disabled={busy} className="hl-button-primary text-xs disabled:opacity-50" data-testid="btn-run-promotion">
            <PlayCircle className="w-3 h-3 inline mr-1" />{busy ? "Running…" : "Run evaluation"}
          </button>
        </div>
      </Section>

      <Section title={`Decisions · ${items.length} students`}>
        <div className="hl-card overflow-hidden">
          <table className="hl-table" data-testid="decisions-table">
            <thead><tr><th>Student</th><th>From</th><th>To</th><th>Overall</th><th>Attendance</th><th>Passed</th><th>Outcome</th><th>Reason codes</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-500">No decisions yet. Run the evaluation above.</td></tr>}
              {items.map(p => (
                <React.Fragment key={p.id}>
                  <tr className={p.approved ? "bg-emerald-50" : (p.student_id === "5adccbfa-7642-423d-88c4-671c5550a517" ? "bg-amber-50/40" : "")}>
                    <td><b data-testid={`row-name-${p.student_id}`}>{p.student_name}</b><div className="text-[10px] text-slate-400 mono">{p.admission_no}</div></td>
                    <td>{p.from_year_group}</td>
                    <td>{p.to_year_group || "—"}</td>
                    <td className="mono">{p.grade_avg == null ? <span className="text-slate-400">—</span> : `${p.grade_avg}%`}</td>
                    <td className="mono">{p.attendance_pct == null ? <span className="text-slate-400">—</span> : `${p.attendance_pct}%`}</td>
                    <td className="mono">{p.subjects_passed}/{p.subjects_total}</td>
                    <td><StatusPill status={p.outcome} /></td>
                    <td className="text-[11px] text-slate-500 max-w-[280px]">
                      {(p.reason_codes || []).length
                        ? (p.reason_codes || []).map(c => (
                            <span key={c} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 mb-0.5 mono">{c}</span>
                          ))
                        : <span className="text-emerald-700">ALL_CRITERIA_MET</span>}
                    </td>
                    <td className="whitespace-nowrap">
                      <button
                        onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                        className="text-xs text-slate-600 hover:underline mr-3"
                        data-testid={`explain-${p.student_id}`}
                      >
                        {expanded === p.id ? "Hide" : "Explain"}
                      </button>
                      {!p.approved && (
                        <button onClick={() => approve(p.id)} className="text-xs text-emerald-700 flex items-center gap-1 inline-flex" data-testid={`approve-${p.id}`}>
                          <CheckCircle2 className="w-3 h-3" />Approve
                        </button>
                      )}
                      {p.approved && <span className="text-xs text-emerald-700 flex items-center gap-1 inline-flex"><CheckCircle2 className="w-3 h-3" />Approved</span>}
                    </td>
                  </tr>
                  {expanded === p.id && (
                    <tr>
                      <td colSpan={9} className="bg-slate-50 p-4">
                        <div className="grid md:grid-cols-2 gap-4 text-xs" data-testid={`explain-body-${p.student_id}`}>
                          <div>
                            <div className="uppercase tracking-widest text-[10px] text-slate-500 font-semibold mb-1">Decision explanation</div>
                            <div className="text-slate-700 mb-3">{p.reason}</div>
                            <div className="uppercase tracking-widest text-[10px] text-slate-500 font-semibold mb-1">Policy · {p.policy_version}</div>
                            <ul className="space-y-0.5 text-slate-600">
                              <li>Minimum overall average: <span className="mono">{p.policy_snapshot?.min_overall_avg}%</span></li>
                              <li>Conditional threshold: <span className="mono">{p.policy_snapshot?.conditional_overall_avg}%</span></li>
                              <li>Minimum subjects passed: <span className="mono">{p.policy_snapshot?.min_subjects_passed}</span></li>
                              <li>Minimum attendance: <span className="mono">{p.policy_snapshot?.min_attendance_pct}%</span></li>
                            </ul>
                            <div className="uppercase tracking-widest text-[10px] text-slate-500 font-semibold mb-1 mt-3" data-testid={`override-history-${p.student_id}`}>Override & Approval History</div>
                            {(p.override_history || []).length === 0 && !p.approved_at ? (
                              <div className="text-[11px] text-slate-400 italic">No overrides or approvals recorded — decision reflects the canonical rule engine only.</div>
                            ) : (
                              <ul className="space-y-1 text-[11px] text-slate-700" data-testid={`override-list-${p.student_id}`}>
                                {(p.override_history || []).map((h, i) => (
                                  <li key={i} className="border-l-2 border-amber-500 pl-2">
                                    <div><b>{h.new_outcome || h.outcome || "—"}</b> <span className="text-slate-400">(was {h.original_outcome || "—"})</span></div>
                                    <div className="text-slate-500">by <b>{h.actor || h.reviewed_by || "—"}</b> · {h.role || "—"} · {(h.timestamp || h.approved_at || "").slice(0, 19).replace("T", " ")}</div>
                                    {h.reason && <div className="italic text-slate-500">"{h.reason}"</div>}
                                    {h.policy_version && <div className="text-slate-400">Policy · {h.policy_version}</div>}
                                  </li>
                                ))}
                                {p.approved_at && (
                                  <li className="border-l-2 border-emerald-500 pl-2">
                                    <div><b>Approved</b> · locked to canonical outcome</div>
                                    <div className="text-slate-500">by <b>{p.reviewed_by || "—"}</b> · {(p.approved_at || "").slice(0, 19).replace("T", " ")}</div>
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>
                          <div>
                            <div className="uppercase tracking-widest text-[10px] text-slate-500 font-semibold mb-1">Subject evidence</div>
                            <table className="w-full text-[11px]">
                              <thead className="text-slate-500"><tr><th className="text-left font-normal">Subject</th><th className="text-right font-normal">Avg</th><th className="text-right font-normal">Grade</th><th className="text-right font-normal">Passed</th></tr></thead>
                              <tbody>
                                {(p.subject_breakdown || []).map(sb => (
                                  <tr key={sb.subject_id}>
                                    <td>{sb.subject_name}</td>
                                    <td className="text-right mono">{sb.average}%</td>
                                    <td className="text-right"><span className={`grade-tag grade-${sb.grade}`}>{sb.grade}</span></td>
                                    <td className="text-right">{sb.passed ? "✓" : "✗"}</td>
                                  </tr>
                                ))}
                                {(!p.subject_breakdown || p.subject_breakdown.length === 0) && (
                                  <tr><td colSpan={4} className="text-slate-400 py-2">No T1/T2 evidence recorded — REVIEW_REQUIRED</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
